// packages import
import { FC, useContext } from 'react'
import {
  Box,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Chip,
  Checkbox,
  IconButton
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { format } from 'date-fns'

// component import
import { CustomPagination } from '../../common/CustomPagination'
import { StickyTable } from '../../common/StickyTable'
import { TableSkeleton } from '../../skeletons/TableSkeleton'
import { BillsMenu } from '../../menus/Bills'

// others
import { BillStatus, UserType } from '../../../../generated'
import { PreBillsListingProps } from '../../../interfaceTypes'
import { capitalizeCase, formatCurrency, hasAdminAccess, isDepartmentHead, removeUnderScore } from '../../../utils'
import { canEditBill, formatStatus, isContingencyCase, isHourlyCase, statusColors } from '../../../utils/billing'
import { Link } from 'react-router-dom'
import { AuthContext } from '../../../context'
import palette from '../../../theme/palette'
import { useDepartments } from '../../../hooks/useDepartments'

const ColumnHeader: FC<{ label: string; info: string }> = ({ label, info }) => (
  <Box display="inline-flex" alignItems="center" gap={0.5}>
    {label}
    <Tooltip title={info} arrow placement="top">
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
    </Tooltip>
  </Box>
)

export const PreBillsListing: FC<PreBillsListingProps> = ({
  preBills,
  loading,
  refetch,
  selectedPreBills,
  setSelectedPreBills
}): JSX.Element => {
  const { isAttorney, currentUser } = useContext(AuthContext)
  const { departments } = useDepartments({})

  const selectedBillIds = selectedPreBills.selectedIds
  const allSelected = selectedPreBills.allSelected
  const hasAdminPermissions = hasAdminAccess(currentUser)
  // Trust/credit columns are accounting-sensitive — only accounting/admins see them.
  const trustMode = hasAdminPermissions

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked

    if (checked) {
      setSelectedPreBills({ selectedIds: preBills?.map(bill => bill.id) || [], allSelected: true })
    } else {
      setSelectedPreBills({ selectedIds: [], allSelected: false })
    }
  }

  const handleRowSelect = (billId: number) => {
    const isSelected = selectedBillIds.includes(billId)

    if (isSelected) {
      const selectedIds = selectedBillIds.filter(id => id !== billId)
      setSelectedPreBills({ selectedIds, allSelected: false })
    } else {
      const newSelection = [...selectedBillIds, billId]
      setSelectedPreBills({ selectedIds: newSelection, allSelected: false })
    }
  }
  const showCheckBox = hasAdminAccess(currentUser) || isAttorney || isDepartmentHead(currentUser, departments)

  return (
    <StickyTable pagination={<CustomPagination loading={loading || false} setParams={true} />} height="400px">
      <TableHead>
        <TableRow>
          {showCheckBox && (
            <TableCell width="5%">
              <Checkbox checked={allSelected} onChange={handleSelectAll} size="small" />
            </TableCell>
          )}
          <TableCell width={showCheckBox ? '8%' : '6%'}> ID </TableCell>
          <TableCell width="10%"> Matter # </TableCell>
          <TableCell width="12%"> Attorney </TableCell>
          <TableCell width="22%"> Matter </TableCell>
          <TableCell width="10%"> Matter Type </TableCell>
          <TableCell width="8%"> Generated On </TableCell>
          <TableCell width="10%">
            <ColumnHeader label="Amount" info="Amount of this pre-bill." />
          </TableCell>
          {trustMode && (
            <>
              <TableCell width="10%">
                <ColumnHeader
                  label="Outstanding Balance"
                  info="Client's total unpaid balance (remaining principal + interest) on prior final bills for this matter."
                />
              </TableCell>
              <TableCell width="9%">
                <ColumnHeader label="Trust" info="Total trust money (IOLTA + escrow) currently held for this matter." />
              </TableCell>
              <TableCell width="9%">
                <ColumnHeader label="Credits" info="Available unapplied client credits for this matter." />
              </TableCell>
              <TableCell width="10%">
                <ColumnHeader
                  label="Pull from Trust"
                  info="Amount to pull from trust to cover this pre-bill plus prior outstanding (Amount + Outstanding Balance − Credits)."
                />
              </TableCell>
            </>
          )}
          <TableCell width="10%"> Status </TableCell>
          <TableCell width="5%"> Action </TableCell>
        </TableRow>
      </TableHead>

      {loading ? (
        <TableSkeleton noOfRows={7} noOfCols={(showCheckBox ? 9 : 8) + (trustMode ? 4 : 0)} />
      ) : (
        <TableBody>
          {preBills?.map(bill => {
            const { id, case: caseData, amount, status, issueDate, trustBalance, creditBalance, outstandingBalance } =
              bill || {}

            const hasTrust = (trustBalance || 0) > 0
            const pullFromTrust = Math.max(0, (amount || 0) + (outstandingBalance || 0) - (creditBalance || 0))

            const { caseNumber, title, billing, caseToUsers, consultMatterNumber } = caseData || {}

            const billApprover = caseData?.billing?.billApprover
            const responsibleAttorney = caseToUsers?.find(
              caseUser => caseUser?.userType === UserType.ResponsibleAttorney_1
            )
            const attorneyName = responsibleAttorney?.user
              ? `${responsibleAttorney.user.firstName || ''} ${responsibleAttorney.user.lastName || ''}`
              : ''

            const formattedStatus = formatStatus(status)
            const isSelected = selectedBillIds.includes(id) || (hasAdminPermissions && allSelected)

            const isCaseParalegal =
              !!currentUser?.id &&
              !!caseData?.caseToUsers?.some(
                ctu => ctu?.userType === UserType.Paralegal && ctu?.user?.id === currentUser.id
              )

            const canEdit =
              bill?.status &&
              currentUser &&
              (canEditBill(
                billApprover?.id || null,
                isDepartmentHead(currentUser, departments),
                bill.status,
                currentUser
              ) ||
                (isCaseParalegal && (bill.status === BillStatus.Draft || bill.status === BillStatus.SentForApproval)))

            return (
              <TableRow key={id} hover selected={isSelected}>
                {showCheckBox && (
                  <TableCell>
                    <Checkbox checked={isSelected} onChange={() => handleRowSelect(id)} size="small" />
                  </TableCell>
                )}

                <TableCell>
                  <Link
                    to={`/billing/bill-detail/${id}`}
                    style={{
                      color: palette.primary.main,
                      textDecoration: 'none'
                    }}
                  >
                    {id}
                  </Link>
                </TableCell>

                <TableCell>
                  {!!(caseNumber || consultMatterNumber) && (
                    <Link
                      to={`/matters/matter-detail/${caseData?.id}`}
                      style={{
                        color: palette.primary.main,
                        textDecoration: 'none'
                      }}
                    >
                      #{caseNumber || consultMatterNumber}
                    </Link>
                  )}
                </TableCell>

                <TableCell>
                  <Typography>{attorneyName}</Typography>
                </TableCell>

                <TableCell>
                  <Tooltip title={title || ''} arrow placement="top">
                    <Typography
                      noWrap
                      sx={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        maxWidth: '350px'
                      }}
                    >
                      {title || ''}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell>
                  <Typography>{capitalizeCase(removeUnderScore(billing?.billingType?.toLowerCase() || ''))}</Typography>
                </TableCell>

                <TableCell>
                  <Typography>{issueDate ? format(new Date(issueDate), 'MM/dd/yyyy') : ''}</Typography>
                </TableCell>

                <TableCell>
                  <Typography>{formatCurrency(amount)}</Typography>
                </TableCell>

                {trustMode && (
                  <>
                    <TableCell>
                      <Typography>{formatCurrency(outstandingBalance || 0)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{hasTrust ? formatCurrency(trustBalance || 0) : '-'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{formatCurrency(creditBalance || 0)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{hasTrust ? formatCurrency(pullFromTrust) : '-'}</Typography>
                    </TableCell>
                  </>
                )}

                <TableCell>
                  <Chip
                    label={formattedStatus}
                    sx={{
                      color: statusColors[status],
                      border: `1px solid ${statusColors[status]}50`,
                      backgroundColor: 'transparent'
                    }}
                  />
                </TableCell>

                <TableCell align="right">
                  <BillsMenu
                    bill={bill}
                    billingType={caseData?.billing?.billingType}
                    caseId={caseData?.id}
                    billApprover={billApprover}
                    isHourlyCase={billing ? isHourlyCase(billing) : false}
                    isContingencyCase={billing ? isContingencyCase(billing) : false}
                    onRefetch={refetch}
                    canEdit={Boolean(canEdit)}
                    renderAction={handleClick => (
                      <IconButton onClick={handleClick}>
                        <MoreVertIcon />
                      </IconButton>
                    )}
                  />
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      )}
    </StickyTable>
  )
}
